import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AerodromeLookup } from './AerodromeLookup.js';
import {
  AircraftRegistrationLookup,
  normalizeRegistration,
  type AircraftIdentity,
} from './AircraftRegistrationLookup.js';
const emptyIdentity = (): AircraftIdentity => ({
  registrationMark: '',
  aircraftKind: '',
  manufacturer: '',
  model: '',
  serialNumber: '',
  legalOwner: '',
  responsibleOperator: '',
});
interface Base {
  id: string;
  name: string;
  airportCode: string | null;
  active: boolean;
}
export function InitialResources({
  organizationId,
  userEmail,
  baseComplete,
  onChanged,
  onExpiredSession,
}: {
  organizationId: string;
  userEmail: string;
  baseComplete: boolean;
  onChanged: () => Promise<void>;
  onExpiredSession: () => Promise<void>;
}) {
  const storageKey = `aeronexo-resources-v2:${organizationId}:${userEmail}`;
  const [identity, setIdentity] = useState<AircraftIdentity>(() => {
    try {
      return (
        (JSON.parse(sessionStorage.getItem(storageKey) || 'null') as AircraftIdentity) ||
        emptyIdentity()
      );
    } catch {
      return emptyIdentity();
    }
  });
  const [verified, setVerified] = useState('');
  const [bases, setBases] = useState<Base[]>([]);
  const [baseIds, setBaseIds] = useState<string[]>([]);
  const [addingBase, setAddingBase] = useState(!baseComplete);
  const [baseName, setBaseName] = useState('');
  const [airportCode, setAirportCode] = useState('');
  const [baseId, setBaseId] = useState(() => crypto.randomUUID());
  const [aircraftId] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(identity));
    } catch {
      /* storage may be unavailable */
    }
  }, [identity, storageKey]);
  const load = useCallback(async () => {
    const r = await fetch(`/v1/organizations/${organizationId}/admin/resources`, {
      credentials: 'include',
    });
    if (r.status === 401) {
      await onExpiredSession();
      return;
    }
    if (!r.ok) throw Error('resources');
    const data = (await r.json()) as { bases: Base[] };
    const active = data.bases.filter((b) => b.active);
    setBases(active);
    setBaseIds((current) =>
      current.length
        ? current.filter((id) => active.some((b) => b.id === id))
        : active.map((b) => b.id),
    );
  }, [organizationId, onExpiredSession]);
  useEffect(() => {
    void load().catch(() => setMessage('Não foi possível carregar as bases. Tente novamente.'));
  }, [load]);
  async function save(event: FormEvent) {
    event.preventDefault();
    setMessage('');
    if (
      !addingBase &&
      (!verified ||
        verified !== normalizeRegistration(identity.registrationMark) ||
        !baseIds.length)
    ) {
      setMessage('Consulte uma matrícula válida no RAB e selecione pelo menos uma base.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(
        `/v1/organizations/${organizationId}/admin/${addingBase ? 'bases/' + baseId : 'aircraft/' + aircraftId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            addingBase
              ? { name: baseName, airportCode: airportCode || null, active: true }
              : { ...identity, baseIds, active: true },
          ),
        },
      );
      if (r.status === 401) {
        await onExpiredSession();
        return;
      }
      if (!r.ok) {
        const error = (await r.json()) as { code?: string };
        setMessage(
          error.code?.startsWith('RAB_')
            ? 'A matrícula não pôde ser confirmada no RAB. Consulte novamente antes de salvar.'
            : r.status === 409
              ? 'Esse cadastro já existe ou foi alterado. Confira os cadastros salvos.'
              : 'Não foi possível salvar. Confira os campos e tente novamente.',
        );
        return;
      }
      if (addingBase) {
        await load();
        setBaseIds((current) => [...new Set([...current, baseId])]);
        setBaseId(crypto.randomUUID());
        setBaseName('');
        setAirportCode('');
        setMessage('Base salva. Você pode cadastrar outra ou continuar para a aeronave.');
      } else {
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          /* optional storage */
        }
        await onChanged();
      }
    } catch {
      setMessage(
        'Não foi possível confirmar o salvamento. Confira os cadastros antes de tentar novamente.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="organizationProfilePanel">
      <p className="registrationEyebrow">Cadastro inicial · etapa {addingBase ? 4 : 5} de 5</p>
      <h1>{addingBase ? 'Suas bases de operação' : 'Sua primeira aeronave'}</h1>
      <p>
        {addingBase
          ? 'Cadastre uma ou mais bases de onde a empresa opera.'
          : 'Comece pela matrícula. Os dados da aeronave serão preenchidos pelo RAB.'}
      </p>
      <form className="profileForm" onSubmit={(e) => void save(e)}>
        <fieldset className="registrationFields" disabled={busy}>
          {addingBase ? (
            <>
              {bases.length ? (
                <ul>
                  {bases.map((b) => (
                    <li key={b.id}>
                      {b.name} {b.airportCode ? `· ${b.airportCode}` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
              <AerodromeLookup
                label="Buscar aeródromo por nome, cidade ou código"
                onExpiredSession={onExpiredSession}
                onSelect={(place) => {
                  setBaseName(place.name);
                  setAirportCode(place.icaoCode || place.iataCode || '');
                }}
              />
              <label>
                Nome da base <span className="requiredMark">*</span>
                <input
                  required
                  minLength={2}
                  maxLength={120}
                  value={baseName}
                  onChange={(e) => setBaseName(e.target.value)}
                />
              </label>
              <label>
                Código ICAO ou local
                <input
                  maxLength={4}
                  pattern="[A-Z0-9]{3,4}"
                  value={airportCode}
                  onChange={(e) => setAirportCode(e.target.value.toUpperCase())}
                />
              </label>
            </>
          ) : (
            <>
              <AircraftRegistrationLookup
                value={identity}
                onChange={setIdentity}
                onVerified={setVerified}
                onExpiredSession={onExpiredSession}
              />
              <fieldset>
                <legend>
                  Bases desta aeronave <span className="requiredMark">*</span>
                </legend>
                {bases.map((b) => (
                  <label className="checkField" key={b.id}>
                    <input
                      type="checkbox"
                      checked={baseIds.includes(b.id)}
                      onChange={(e) =>
                        setBaseIds((ids) =>
                          e.target.checked ? [...ids, b.id] : ids.filter((id) => id !== b.id),
                        )
                      }
                    />
                    {b.name} {b.airportCode ? `· ${b.airportCode}` : ''}
                  </label>
                ))}
              </fieldset>
              <button type="button" onClick={() => setAddingBase(true)}>
                Cadastrar outra base
              </button>
            </>
          )}
        </fieldset>
        {message ? <p role="status">{message}</p> : null}
        <div className="registrationActions">
          <button
            className="primaryButton"
            type="submit"
            disabled={busy || (!addingBase && (!verified || !baseIds.length))}
          >
            {busy ? 'Salvando…' : addingBase ? 'Salvar base' : 'Salvar aeronave e entrar'}
          </button>
          {addingBase && bases.length ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setAddingBase(false);
                setMessage('');
              }}
            >
              Continuar para aeronave
            </button>
          ) : null}
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              void load().catch(() => setMessage('Não foi possível carregar. Tente novamente.'))
            }
          >
            Verificar cadastros salvos
          </button>
        </div>
      </form>
    </section>
  );
}
